'use client';

import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

export type SavedListing = {
  id: string;
  href: string;
  title: string;
  price: number;
  imageUrl: string;
  bedDisplay?: string;
  bathDisplay?: string;
  neighborhood?: string;
  topAmenities?: Array<string>;
  latitude?: number;
  longitude?: number;
  savedAt: number;
};

type SavedListingsContextValue = {
  savedListings: Array<SavedListing>;
  count: number;
  saveListing: (listing: SavedListing) => void;
  removeListing: (id: string) => void;
  isSaved: (id: string) => boolean;
  clearAll: () => void;
};

const SavedListingsContext = createContext<SavedListingsContextValue | null>(null);

const STORAGE_KEY = 'halo-saved-listings';

export const SavedListingsProvider = ({ children }: { children: React.ReactNode }) => {
  const [savedListings, setSavedListings] = useState<Array<SavedListing>>([]);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Array<SavedListing>;
        const sorted = parsed.sort((a, b) => b.savedAt - a.savedAt);
        setSavedListings(sorted);
      }
    } catch (error) {
      console.warn('Failed to load saved listings from sessionStorage:', error);
      setSavedListings([]);
    }
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(savedListings));
    } catch (error) {
      console.warn('Failed to save listings to sessionStorage:', error);
    }
  }, [savedListings]);

  const saveListing = (listing: SavedListing) => {
    setSavedListings((prev) => {
      const filtered = prev.filter((item) => item.id !== listing.id);
      return [listing, ...filtered];
    });
  };

  const removeListing = (id: string) => {
    setSavedListings((prev) => prev.filter((item) => item.id !== id));
  };

  const isSaved = (id: string): boolean => {
    return savedListings.some((item) => item.id === id);
  };

  const clearAll = () => {
    setSavedListings([]);
  };

  const value: SavedListingsContextValue = {
    savedListings,
    count: savedListings.length,
    saveListing,
    removeListing,
    isSaved,
    clearAll,
  };

  return <SavedListingsContext.Provider value={value}>{children}</SavedListingsContext.Provider>;
};

export const useSavedListings = (): SavedListingsContextValue => {
  const context = useContext(SavedListingsContext);

  if (!context) {
    throw new Error('useSavedListings must be used within a SavedListingsProvider');
  }

  return context;
};
